<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>RED Bills</h1>
        <button @click="printRedBills" class="print-btn">
          <i class="pi pi-print"></i> Print
        </button>
      </div>

      <DataTable 
        :value="redBills" 
        :loading="loading"
        stripedRows
        showGridlines
        responsiveLayout="scroll"
        :globalFilterFields="['subject_title', 'tier', 'period_of_congress']"
        v-model:filters="filters"
        filterDisplay="menu"
      >
        <template #header>
          <div class="table-header">
            <h3>RED Bills ({{ redBills.length }})</h3>
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search bills..." />
            </IconField>
          </div>
        </template>
      

      
      <ColumnGroup type="header">
        <Row>
          <Column header="Subject Title" :rowspan="2" sortable field="subject_title" />
          <Column header="House Totals" :colspan="2" />
          <Column header="Senate Totals" :colspan="2" />
          <Column header="Bicam Totals" :colspan="2" />
        </Row>
        <Row>
          <Column header="Revenue" sortable field="house_revenue_total" />
          <Column header="Budgetary" sortable field="house_budgetary_total" />
          <Column header="Revenue" sortable field="senate_revenue_total" />
          <Column header="Budgetary" sortable field="senate_budgetary_total" />
          <Column header="Revenue" sortable field="bicam_revenue_total" />
          <Column header="Budgetary" sortable field="bicam_budgetary_total" />
        </Row>
      </ColumnGroup>
      
      <ColumnGroup type="footer">
        <Row>
          <Column footer="GRAND TOTALS" class="grand-total-label" />
          <Column :footer="formatAmount(grandTotals.house_revenue)" class="grand-total" />
          <Column :footer="formatAmount(grandTotals.house_budgetary)" class="grand-total" />
          <Column :footer="formatAmount(grandTotals.senate_revenue)" class="grand-total" />
          <Column :footer="formatAmount(grandTotals.senate_budgetary)" class="grand-total" />
          <Column :footer="formatAmount(grandTotals.bicam_revenue)" class="grand-total" />
          <Column :footer="formatAmount(grandTotals.bicam_budgetary)" class="grand-total" />
        </Row>
      </ColumnGroup>
      
      
      <Column field="subject_title" sortable :showFilterMatchModes="false">
        <template #body="{ data }">
          <router-link :to="`subject/view/${data.id}`" class="subject-link">
            {{ data.subject_title }}
          </router-link>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search title" />
        </template>
      </Column>
      
      <Column field="house_revenue_total" sortable>
        <template #body="{ data }">
          <div class="amount-cell">{{ formatAmount(data.house_revenue_total) }}</div>
        </template>
      </Column>
      
      <Column field="house_budgetary_total" sortable>
        <template #body="{ data }">
          <div class="amount-cell">{{ formatAmount(data.house_budgetary_total) }}</div>
        </template>
      </Column>
      
      <Column field="senate_revenue_total" sortable>
        <template #body="{ data }">
          <div class="amount-cell">{{ formatAmount(data.senate_revenue_total) }}</div>
        </template>
      </Column>
      
      <Column field="senate_budgetary_total" sortable>
        <template #body="{ data }">
          <div class="amount-cell">{{ formatAmount(data.senate_budgetary_total) }}</div>
        </template>
      </Column>
      
      <Column field="bicam_revenue_total" sortable>
        <template #body="{ data }">
          <div class="amount-cell">{{ formatAmount(data.bicam_revenue_total) }}</div>
        </template>
      </Column>
      
      <Column field="bicam_budgetary_total" sortable>
        <template #body="{ data }">
          <div class="amount-cell">{{ formatAmount(data.bicam_budgetary_total) }}</div>
        </template>
      </Column>
      

      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import api from '../axios.js'

const redBills = ref([])
const loading = ref(false)
const grandTotals = ref({
  house_revenue: 0,
  house_budgetary: 0,
  senate_revenue: 0,
  senate_budgetary: 0,
  bicam_revenue: 0,
  bicam_budgetary: 0
})

const filters = ref({
  global: { value: null, matchMode: 'contains' },
  subject_title: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})

const loadRedBills = async () => {
  try {
    loading.value = true
    const response = await api.get('/red-bills')
    redBills.value = response.data.sort((a, b) => {
      const totalA = (a.house_revenue_total || 0) + (a.house_budgetary_total || 0) + 
                     (a.senate_revenue_total || 0) + (a.senate_budgetary_total || 0) + 
                     (a.bicam_revenue_total || 0) + (a.bicam_budgetary_total || 0)
      const totalB = (b.house_revenue_total || 0) + (b.house_budgetary_total || 0) + 
                     (b.senate_revenue_total || 0) + (b.senate_budgetary_total || 0) + 
                     (b.bicam_revenue_total || 0) + (b.bicam_budgetary_total || 0)
      return totalB - totalA
    })
    
    // Calculate grand totals
    grandTotals.value = redBills.value.reduce((totals, bill) => {
      totals.house_revenue += bill.house_revenue_total || 0
      totals.house_budgetary += bill.house_budgetary_total || 0
      totals.senate_revenue += bill.senate_revenue_total || 0
      totals.senate_budgetary += bill.senate_budgetary_total || 0
      totals.bicam_revenue += bill.bicam_revenue_total || 0
      totals.bicam_budgetary += bill.bicam_budgetary_total || 0
      return totals
    }, {
      house_revenue: 0,
      house_budgetary: 0,
      senate_revenue: 0,
      senate_budgetary: 0,
      bicam_revenue: 0,
      bicam_budgetary: 0
    })
  } catch (error) {
    console.error('Error loading RED bills:', error)
    redBills.value = []
  } finally {
    loading.value = false
  }
}

const formatAmount = (amount) => {
  if (!amount || amount === 0) return '0'
  return new Intl.NumberFormat('en-US').format(amount)
}

const printRedBills = async () => {
  try {
    const response = await api.get('/red-bills/print', { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
  } catch (error) {
    console.error('Error printing RED bills:', error)
  }
}

onMounted(() => {
  loadRedBills()
})

</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}


.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #dc2626;
}

.page-header h1 {
  color: #dc2626;
  font-size: 2rem;
  font-weight: 700;
}

.print-btn {
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
}

.print-btn:hover {
  background: #b91c1c;
  transform: translateY(-1px);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #dc2626;
  margin: 0;
}



.amount-cell {
  text-align: right;
  font-weight: 500;
  color: #374151;
}

.subject-link {
  color: #dc2626;
  text-decoration: none;
  font-weight: 500;
}

.subject-link:hover {
  text-decoration: underline;
  color: #b91c1c;
}

:deep(.grand-total) {
  font-weight: 700 !important;
  color: #dc2626 !important;
  background: #fef2f2 !important;
  text-align: right !important;
}

:deep(.grand-total-label) {
  font-weight: 700 !important;
  color: #dc2626 !important;
  background: #fef2f2 !important;
  text-align: center !important;
}

@media (max-width: 768px) {
  .page {
    padding: 1rem;
  }
}
</style>